import { NutritionalRequirementsUpdate } from "@/clients/models/NutritionalRequirementsUpdate";
import { RoundedModal } from "@/components/modal/rounded-modal";
import { ScrollPicker, PickerValue } from "@/components/scroll-picker";
import { useGetCalories } from "@/hooks/requests/nutritional-requirement/use-get-calories";
import { useUpdateCalories } from "@/hooks/requests/nutritional-requirement/use-update-calories";
import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { memo, useEffect, useMemo, useState } from "react";

interface EditDailyCaloriesModalProps {
  isOpen: boolean;
  onDismiss: () => void;
}

const MIN_CALORIES = 500;
const MAX_CALORIES = 5000;
const CALORIE_STEP = 50;

const FEMALE_MIN_CALORIES = 1200;
const FEMALE_MAX_CALORIES = 2400;
const MALE_MIN_CALORIES = 1600;
const MALE_MAX_CALORIES = 3000;

const calorieOptions: string[] = (() => {
  const options: string[] = [];
  for (let i = MIN_CALORIES; i <= MAX_CALORIES; i += CALORIE_STEP) {
    options.push(String(i));
  }
  return options;
})();

const renderItem = (value: string, selected: boolean) => (
  <div
    className={cn(
      "flex h-[56px] w-full items-center justify-center text-[22px] font-semibold",
      selected ? "text-[#43246e]" : "text-[#7485a7]",
    )}
  >
    {value}
  </div>
);

export const EditDailyCaloriesModal = memo(function EditDailyCaloriesModal({
  isOpen,
  onDismiss,
}: EditDailyCaloriesModalProps) {
  const { showError } = useToast();
  const { user } = useGetUser();
  const { calories, isLoading: isCaloriesLoading } = useGetCalories();
  const { updateCaloriesAsync, isUpdating: isUpdatingCalories } = useUpdateCalories();

  const currentCalories = useMemo(
    () => parseInt(calories?.dailyCalories?.toString() ?? String(MIN_CALORIES)) || MIN_CALORIES,
    [calories],
  );

  const [pickerValue, setPickerValue] = useState<PickerValue>(() => {
    const nearest =
      calorieOptions.find((opt) => Number(opt) >= currentCalories) ?? String(MIN_CALORIES);
    return { calories: nearest };
  });

  useEffect(() => {
    if (currentCalories) {
      const nearest =
        calorieOptions.find((opt) => Number(opt) >= currentCalories) ?? String(MIN_CALORIES);
      setPickerValue({ calories: nearest });
    }
  }, [currentCalories]);

  const handleConfirm = async () => {
    const selectedCalories = Number(pickerValue.calories);
    const userSex = user?.personalInfo?.sex?.toLowerCase();

    if (userSex === "female") {
      if (selectedCalories < FEMALE_MIN_CALORIES) {
        showError(`The minimum daily calorie target for females is ${FEMALE_MIN_CALORIES} kcal`);
        return;
      }
      if (selectedCalories > FEMALE_MAX_CALORIES) {
        showError(`The maximum daily calorie target for females is ${FEMALE_MAX_CALORIES} kcal`);
        return;
      }
    } else if (userSex === "male") {
      if (selectedCalories < MALE_MIN_CALORIES) {
        showError(`The minimum daily calorie target for males is ${MALE_MIN_CALORIES} kcal`);
        return;
      }
      if (selectedCalories > MALE_MAX_CALORIES) {
        showError(`The maximum daily calorie target for males is ${MALE_MAX_CALORIES} kcal`);
        return;
      }
    }

    const dataBody: NutritionalRequirementsUpdate = {
      dailyCalories: selectedCalories,
    };
    try {
      await updateCaloriesAsync(dataBody, {
        onSuccess: () => {
          onDismiss();
        },
        onError: (error) => {
          showError(error.message);
        },
      });
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("An unexpected error occurred");
      }
    }
  };

  return (
    <RoundedModal
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      backdropDismiss
      width="92%"
      maxWidth="420px"
      borderRadius="34px"
    >
      <div className="safe-area-bottom flex flex-col items-center gap-4 px-9 pt-12 pb-10">
        <h3 className="text-center text-[18px] font-bold text-black">
          Daily Calorie Target (kcal)
        </h3>

        <div className="w-full max-w-[322px]">
          <ScrollPicker
            value={pickerValue}
            onChange={setPickerValue}
            height={168}
            itemHeight={56}
          >
            <ScrollPicker.Column name="calories" options={calorieOptions} renderItem={renderItem} />
          </ScrollPicker>
        </div>

        <button
          type="button"
          onClick={handleConfirm}
          disabled={isUpdatingCalories || isCaloriesLoading}
          className="flex h-[50px] w-full max-w-[323px] items-center justify-center rounded-[40px] bg-[linear-gradient(to_bottom,#513775,#43246e)] text-base font-medium text-white shadow-md disabled:opacity-50"
        >
          {isUpdatingCalories ? "Saving..." : "Confirm Change"}
        </button>
      </div>
    </RoundedModal>
  );
});
